import {
  GQL_PRIVATE_STATUSPAGE_PDF_QUERY,
  GQL_PUBLIC_STATUSPAGE_PDF_QUERY,
} from "@/lib/queries/statusPageConsumption.ts";
import {useLazyQuery} from "@apollo/client";
import {endOfDay, format, subDays} from "date-fns";
import {useCallback} from "react";

interface UseStatusPagePdfDownloadOptions {
  mode: "public" | "private";
  slug: string;
  pageId: number | undefined;
  defaultHistoryDateRange: number;
}

export const useStatusPagePdfDownload = ({
  mode,
  slug,
  pageId,
  defaultHistoryDateRange,
}: UseStatusPagePdfDownloadOptions) => {
  const [fetchPublicPdf, {loading: publicPdfLoading}] = useLazyQuery<{
    publicStatusPagePdf: {data: string; filename: string} | null;
  }>(GQL_PUBLIC_STATUSPAGE_PDF_QUERY, {
    fetchPolicy: "no-cache",
    context: {statusPageSlug: slug, skipAuthError: true},
  });

  const [fetchPrivatePdf, {loading: privatePdfLoading}] = useLazyQuery<{
    privateStatusPagePdf: {data: string; filename: string} | null;
  }>(GQL_PRIVATE_STATUSPAGE_PDF_QUERY, {fetchPolicy: "no-cache"});

  const loading = publicPdfLoading || privatePdfLoading;

  const downloadPdf = useCallback(
    (range?: {startDate: string; endDate: string}) => {
      let startDate: string;
      let endDate: string;
      if (range) {
        ({startDate, endDate} = range);
      } else {
        // Fallback range when no explicit range is supplied (e.g. programmatic download).
        const days = defaultHistoryDateRange || 7;
        const now = new Date();
        const end = endOfDay(now);
        const start = subDays(end, days);
        startDate = format(start, "yyyyMMdd");
        endDate = format(end, "yyyyMMdd");
      }

      // Open a blank window synchronously to preserve user activation — browsers
      // block downloads triggered from async callbacks without a user gesture.
      // Cannot use noopener feature string here as it causes window.open to return
      // null per spec; instead we manually null out opener to prevent tabnabbing.
      const downloadWindow = window.open("", "_blank");
      if (downloadWindow) downloadWindow.opener = null;

      const onCompleted = (result: {data: string; filename: string} | null) => {
        if (!result) {
          downloadWindow?.close();
          return;
        }
        const binary = atob(result.data);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) {
          bytes[i] = binary.charCodeAt(i);
        }
        const blob = new Blob([bytes], {type: "application/pdf"});
        const url = URL.createObjectURL(blob);
        // Revoke only after the blob has been consumed. Revoking synchronously
        // races the browser: navigating the popup to a just-revoked URL yields a
        // blank tab, and anchor downloads are aborted before the blob is read
        // (Firefox). Both make the button intermittently do nothing.
        const revokeLater = () => window.setTimeout(() => URL.revokeObjectURL(url), 60_000);

        if (downloadWindow && !downloadWindow.closed) {
          downloadWindow.location.href = url;
        } else {
          // Fallback if the popup was blocked (or blocked-then-closed).
          const link = document.createElement("a");
          link.href = url;
          link.download = result.filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
        revokeLater();
      };

      if (mode === "private" && pageId != null) {
        fetchPrivatePdf({variables: {id: pageId, startDate, endDate}})
          .then((res) => onCompleted(res.data?.privateStatusPagePdf ?? null))
          .catch(() => downloadWindow?.close());
      } else {
        fetchPublicPdf({variables: {slug, startDate, endDate}})
          .then((res) => onCompleted(res.data?.publicStatusPagePdf ?? null))
          .catch(() => downloadWindow?.close());
      }
    },
    [defaultHistoryDateRange, mode, pageId, slug, fetchPublicPdf, fetchPrivatePdf]
  );

  return {downloadPdf, loading};
};
